<script setup lang="ts">
import {
	useAddPublicPageSite,
	useDeletePublicPage,
	useDeletePublicPageAvatar,
	useRemovePublicPageSite,
	useUpdatePublicPage,
	useUpdatePublicPageAvatar,
} from "@/api/public-page-query";
import { usePublicPageSites } from "@/api/site-query";
import { wheneverOnce } from "@/composable/whenever-once";
import ConfigSection from "@/layouts/content/ConfigSection.vue";
import { useSnackbarStore } from "@/stores";
import { useQueryManager } from "@/stores/query-manager";
import { useConfirmLeave } from "@/use/confirm-leave";
import {
	mdiAccountCircle,
	mdiAccountCircleOutline,
	mdiContentCopy,
	mdiDelete,
	mdiLinkOff,
	mdiViewDashboardEditOutline,
} from "@mdi/js";
import { useForm } from "@soft-db/form";
import { PublicPage, Site, UpdatePublicPage } from "@soft-db/monitoring-api-client";
import {
	Badge,
	Button,
	confirm,
	Divider,
	FormField,
	Icon,
	InputGroup,
	LinkButton,
	TextInput,
	Tooltip,
} from "@soft-db/ui";
import * as _ from "lodash-es";
import AppAvatar from "../common/AppAvatar.vue";
import DesktopOnly from "../common/DesktopOnly.vue";
import SettingsFooter from "../common/SettingsFooter.vue";
import FileSelectHelper from "../helpers/FileSelectHelper.vue";
import LanguageSelect from "../helpers/LanguageSelect.vue";
import PermissionGuard from "../permission/PermissionGuard.vue";
import SitesRelationTable from "../sites/SitesRelationTable.vue";

const props = defineProps<{
	page: PublicPage;
}>();

const { t } = useI18n();

const snackbar = useSnackbarStore();

const form = useForm<UpdatePublicPage>();

const name = form.useField("name", { isRequired: true, min: 1 });
const preferredLang = form.useField("preferredLang", { isRequired: true });

function syncPage(page: PublicPage) {
	form.setValues({
		name: page.name,
		preferredLang: page.preferredLang,
	});
}

wheneverOnce(
	() => props.page,
	() => syncPage(props.page),
	{ immediate: true }
);

function isEqual(update: UpdatePublicPage, user: PublicPage) {
	for (const key in update) {
		if (!_.isEqual(update[key], user[key])) return false;
	}

	return true;
}

const hasChanges = computed(() => {
	return !isEqual({ ...form.values.value }, props.page);
});

const updatePageMut = useUpdatePublicPage();
const updateAvatarMut = useUpdatePublicPageAvatar();
const deleteAvatarMut = useDeletePublicPageAvatar();
const deletePageMut = useDeletePublicPage();

const currentSites = ref<Site[]>([]);

const { sites, isLoading: sitesLoading } = usePublicPageSites(() => props.page.id);

function syncSites(sites: Site[]) {
	currentSites.value = sites;
}

wheneverOnce(
	sites,
	(sites) => {
		syncSites(sites);
	},
	{ immediate: true }
);

const addSiteMut = useAddPublicPageSite();
const removeSiteMut = useRemovePublicPageSite();

const queryManager = useQueryManager();

function undoChanges() {
	syncPage(props.page);
}

const saveChanges = form.handleSubmit(async (page) => {
	await queryManager.chainInvalidation(async () => {
		await updatePageMut.mutateAsync({
			id: props.page.id,
			data: page,
		});
	});

	syncPage(props.page);

	snackbar.openOnSaved();
});

async function addSites(sites: Site[]) {
	await queryManager.chainInvalidation(async () => {
		for (const site of sites) {
			await addSiteMut.mutateAsync({
				id: props.page.id,
				site: site.id,
			});
		}
	});

	snackbar.openOnSaved();
}

async function removeSite(site: Site) {
	const confirmed = await confirm.yesNo(t("publicPage.removeSiteConfirmation", { n: site.name }));
	if (!confirmed) {
		return;
	}

	await removeSiteMut.mutateAsync({
		id: props.page.id,
		site: site.id,
	});

	snackbar.openOnSaved();
}

async function updateAvatar(file: File) {
	await updateAvatarMut.mutateAsync({
		id: props.page.id,
		image: file,
	});

	snackbar.openOnSaved();
}

async function deleteAvatar() {
	await deleteAvatarMut.mutateAsync({
		id: props.page.id,
	});

	snackbar.openOnSaved();
}

const router = useRouter();
async function deletePublicPage() {
	const confirmed = await confirm.delete(t("publicPage.deleteConfirm", { n: props.page.name }));
	if (!confirmed) {
		return;
	}

	await deletePageMut.mutateAsync(props.page.id);
	router.back();
}

const publicURL = computed(() => {
	return `${window.location.origin}/?public_token=${props.page.uniqueToken}`;
});

async function copyPublicURLToClipboard() {
	await navigator.clipboard.writeText(publicURL.value);
}

useConfirmLeave(() => hasChanges.value, t("common.unsavedChanges"));
</script>

<template>
	<ConfigSection :title="t('object.publicPage')" :description="t('publicPage.settings')">
		<div class="flex items-center gap-4">
			<FileSelectHelper
				class="flex size-48 min-w-48 items-center justify-center rounded-full"
				:showDelete="!!page.avatarUrl"
				@update="updateAvatar"
				@delete="deleteAvatar"
			>
				<AppAvatar
					class="size-full"
					outlined
					:src="page.avatarUrl || ''"
					:icon="mdiAccountCircle"
				/>
			</FileSelectHelper>
			<div>
				<h5>{{ t("common.name") }}</h5>
				<Badge class="h-fit flex-wrap">{{ page?.name }}</Badge>
			</div>
		</div>

		<FormField :label="t('client.publicLink')">
			<InputGroup>
				<TextInput class="muted" :modelValue="publicURL" readonly />
				<Tooltip :tooltip="t('common.copyToClipboard')">
					<Button variant="soft" color="primary" size="xs" @click="copyPublicURLToClipboard">
						<Icon :d="mdiContentCopy" />
					</Button>
				</Tooltip>
			</InputGroup>
		</FormField>

		<FormField class="w-full" :label="t('common.name')" :errors="name.errors.value">
			<InputGroup class="w-full">
				<Icon :d="mdiAccountCircleOutline" />
				<TextInput v-model="name.data.value" />
			</InputGroup>
		</FormField>

		<LanguageSelect v-model="preferredLang.data.value" :label="t('profile.language')" />
	</ConfigSection>

	<Divider />

	<ConfigSection :title="t('object.project', 2)" :description="t('publicPage.sites')">
		<SitesRelationTable
			:scope="page.scope"
			:loading="sitesLoading"
			:sites="sites || []"
			@add="addSites"
		>
			<template #action="{ site }">
				<DesktopOnly>
					<Tooltip :tooltip="t('publicPage.configureDashboard')">
						<span>
							<LinkButton
								variant="soft"
								color="primary"
								:to="{
									name: 'dashboard-id',
									params: { id: site.id },
									query: { publicPage: props.page.id },
								}"
							>
								<Icon :d="mdiViewDashboardEditOutline" />
							</LinkButton>
						</span>
					</Tooltip>
				</DesktopOnly>
				<Tooltip :tooltip="t('space.unlinkSite')">
					<Button color="error" variant="soft" @click="removeSite(site)">
						<Icon :d="mdiLinkOff" />
					</Button>
				</Tooltip>
			</template>
		</SitesRelationTable>
	</ConfigSection>

	<SettingsFooter
		class="mt-4"
		:hasChanges="hasChanges"
		:loading="form.submitting.value"
		@undo="undoChanges"
		@save="saveChanges"
	>
		<template #prepend>
			<DesktopOnly>
				<PermissionGuard permission="public-page.delete">
					<Button
						v-if="!hasChanges"
						variant="ghost"
						size="xs"
						color="error"
						@click="deletePublicPage"
					>
						<Icon :d="mdiDelete" />
						{{ t("publicPage.delete") }}
					</Button>
				</PermissionGuard>
			</DesktopOnly>
		</template>
	</SettingsFooter>
</template>
